import { initializeApp } from 'firebase/app';
import { getAuth, setPersistence, browserLocalPersistence, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

// Your Firebase configuration
// ⚠️ SECURITY: All Firebase config values must come from environment variables
// Never hardcode API keys or sensitive values in source code

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// ============================================================
// DEFENSIVE GUARD: Prevent blank-page crash if env vars missing
// ============================================================
const REQUIRED_FIREBASE_VARS = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_AUTH_DOMAIN',
  'REACT_APP_FIREBASE_PROJECT_ID',
  'REACT_APP_FIREBASE_APP_ID',
] as const;

const missingVars = REQUIRED_FIREBASE_VARS.filter(
  (key) => !process.env[key]
);

if (missingVars.length > 0) {
  const errorMsg = `🚨 FATAL: Missing Firebase environment variables: ${missingVars.join(', ')}. ` +
    `The app cannot start. If deployed on Vercel, add these variables in Project Settings → Environment Variables and redeploy.`;
  
  console.error(errorMsg);

  // Show a visible error instead of a blank page
  if (typeof document !== 'undefined') {
    const root = document.getElementById('root');
    if (root) {
      root.innerHTML = `
        <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;background:#0f172a;color:#f1f5f9;font-family:Inter,system-ui,sans-serif;padding:2rem;">
          <div style="max-width:560px;text-align:center;">
            <div style="font-size:4rem;margin-bottom:1rem;">⚙️</div>
            <h1 style="font-size:1.5rem;font-weight:700;margin-bottom:0.75rem;color:#f87171;">Configuration Error</h1>
            <p style="color:#94a3b8;line-height:1.6;margin-bottom:1.5rem;">
              Scubamail could not start because required environment variables are missing.
              This is a deployment configuration issue, not a bug.
            </p>
            <div style="background:#1e293b;border-radius:8px;padding:1rem;text-align:left;font-family:monospace;font-size:0.8rem;color:#fbbf24;word-break:break-all;">
              Missing: ${missingVars.join(', ')}
            </div>
            <p style="color:#64748b;font-size:0.8rem;margin-top:1rem;">
              Add these variables in Vercel → Project Settings → Environment Variables, then redeploy.
            </p>
          </div>
        </div>`;
    }
  }
}

// Debug: Log environment variable loading (only in development)
if (process.env.NODE_ENV === 'development') {
  const isEnvVarLoaded = !!process.env.REACT_APP_FIREBASE_API_KEY;

  if (!isEnvVarLoaded) {
    console.warn('⚠️ WARNING: Firebase environment variables not loaded!');
    console.warn('This means your .env file might be missing or incorrectly configured. Check:');
    console.warn('1. .env file exists in project root');
    console.warn('2. Variable names start with REACT_APP_');
  } else {
    console.log('✅ Firebase environment variables loaded successfully');
  }
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase Authentication and get a reference to the service
export const auth = getAuth(app);

// browserLocalPersistence is the default for web — no need to set it explicitly.
// Calling setPersistence in every tab (including public preview tabs) caused cross-tab
// auth state events that briefly logged out the original authenticated tab.

// ============================================================
// Firebase App Check
// ============================================================
// App Check attaches a signed attestation token to every Firestore/Functions
// request, proving it came from this real web app (verified via reCAPTCHA v3)
// rather than a script hitting our endpoints directly. This is the strongest
// available defense against bot floods hitting public Cloud Functions and
// Firestore rules that allow unauthenticated create/read (signup forms,
// tracking pixel, unsubscribe, status page, etc.) — rate limiting and
// maxInstances caps reduce the *cost* of abuse, but App Check is what
// actually distinguishes real users from bots before a request is processed.
//
// Setup required (one-time, in Firebase Console):
//   1. Console → App Check → register this web app → choose reCAPTCHA v3.
//   2. Create a reCAPTCHA v3 site key at https://www.google.com/recaptcha/admin
//      for this domain, and set REACT_APP_RECAPTCHA_V3_SITE_KEY.
//   3. Console → App Check → for each Cloud Function / Firestore, toggle
//      "Enforce" once you've confirmed real traffic carries valid tokens
//      (start in "Unenforced/Monitor" mode to avoid locking out real users).
// Until REACT_APP_RECAPTCHA_V3_SITE_KEY is set, this is a no-op — the app
// works exactly as before, just without the extra attestation token.
const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_V3_SITE_KEY;
if (recaptchaSiteKey) {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true,
    });
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Firebase App Check initialized');
    }
  } catch (error) {
    console.error('❌ Error initializing Firebase App Check:', error);
  }
} else if (process.env.NODE_ENV === 'development') {
  console.warn(
    '⚠️ REACT_APP_RECAPTCHA_V3_SITE_KEY not set — Firebase App Check is disabled. ' +
    'See src/firebase.ts for setup instructions.'
  );
}

// Initialize Cloud Firestore and get a reference to the service
export const db = getFirestore(app);

// Initialize Firebase Storage — uses storageBucket from firebaseConfig (REACT_APP_FIREBASE_STORAGE_BUCKET)
export const storage = getStorage(app);

// Initialize Firebase Functions and get a reference to the service
export const functions = getFunctions(app, 'us-central1');

// Connect to emulators in development mode ONLY if explicitly requested
const useEmulator = process.env.REACT_APP_USE_FIREBASE_EMULATOR === 'true';

if (process.env.NODE_ENV === 'development' && useEmulator) {
  console.log('🔧 Connecting to Firebase Emulators...');
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
  connectFirestoreEmulator(db, '127.0.0.1', 8080);
  connectAuthEmulator(auth, 'http://127.0.0.1:9099');
} else if (process.env.NODE_ENV === 'development') {
  console.log('🌐 Development mode: Connecting to live Firebase services (Emulator disabled)');
}

export default app;
